import React, { useEffect, useState } from "react";
import { useSession } from "../lib/session";
import { listAnnouncements, postAnnouncement, deleteAnnouncement } from "../lib/api";
import { Field, Notice } from "./ui";

/**
 * The organiser's channel. Society-wide by default; pass a competitionId
 * and posts made from there are tied to that competition, so they drop
 * off the general list once it's done.
 */
export default function Announcements({ competitionId = null, limit = null, compact = false }) {
  const { society, profile, isOrganiser } = useSession();
  const [rows, setRows] = useState(null);
  const [writing, setWriting] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", pinned: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () =>
    listAnnouncements(society.id, competitionId)
      .then(setRows)
      .catch((e) => setError(e.message));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [society.id, competitionId]);

  async function submit(e) {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await postAnnouncement(society.id, profile.id, { ...form, competitionId });
      setForm({ title: "", body: "", pinned: false });
      setWriting(false);
      await load();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this notice?")) return;
    try { await deleteAnnouncement(id); await load(); }
    catch (e) { setError(e.message); }
  }

  if (!rows) return null;
  const shown = limit ? rows.slice(0, limit) : rows;

  return (
    <>
      <div className="row" style={{ marginBottom: ".6rem" }}>
        <h2 style={{ fontSize: ".9375rem", fontWeight: 700 }}>From the organisers</h2>
        <span className="spacer" />
        {isOrganiser && !writing && (
          <button className="btn-link" onClick={() => setWriting(true)}>Post a notice</button>
        )}
      </div>

      <Notice kind="error">{error}</Notice>

      {writing && (
        <form className="surface" style={{ padding: "1rem", marginBottom: ".7rem" }} onSubmit={submit}>
          <Field label="Title">
            <input className="input" required autoFocus value={form.title}
                   onChange={(e) => setForm({ ...form, title: e.target.value })}
                   placeholder="Tee times for Saturday" />
          </Field>
          <Field label="Notice"
                 hint={competitionId
                   ? "Shown on this competition only."
                   : "Shown to the whole society."}>
            <textarea className="input" required rows={4} value={form.body}
                      onChange={(e) => setForm({ ...form, body: e.target.value })}
                      placeholder="First group away at 08:12…" />
          </Field>
          <label className="row" style={{ marginBottom: ".8rem", cursor: "pointer" }}>
            <input type="checkbox" checked={form.pinned}
                   onChange={(e) => setForm({ ...form, pinned: e.target.checked })} />
            <span style={{ fontSize: ".875rem" }}>Pin to the top</span>
          </label>
          <div className="row">
            <button className="btn btn-primary" disabled={busy}>
              {busy ? "Posting…" : "Post"}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setWriting(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {shown.length === 0 ? (
        !writing && (
          <div className="empty" style={{ padding: "1.5rem 1rem" }}>
            <p style={{ marginBottom: 0 }}>
              {isOrganiser
                ? "Nothing posted yet. This is where tee times and subs go."
                : "No notices at the moment."}
            </p>
          </div>
        )
      ) : (
        <div className="stack" style={{ gap: ".55rem" }}>
          {shown.map((a) => (
            <article key={a.id}
                     className={`notice-card${a.pinned ? " pinned" : ""}${
                       /^Hole in one/.test(a.title) ? " ace" : ""}`}>
              <div className="row" style={{ alignItems: "baseline" }}>
                {/^Hole in one/.test(a.title) && (
                  <span className="ace-flag" aria-hidden="true" />
                )}
                {a.pinned && <span className="pill pill-mark">Pinned</span>}
                <span className="muted" style={{ fontSize: ".75rem" }}>
                  {a.profiles?.full_name} · {new Date(a.created_at).toLocaleDateString("en-GB",
                    { day: "numeric", month: "short" })}
                  {a.competition_id && " · this competition"}
                </span>
                <span className="spacer" />
                {isOrganiser && (
                  <button className="btn-link" onClick={() => remove(a.id)}
                          style={{ color: "var(--dim)" }}>Delete</button>
                )}
              </div>
              <h3>{a.title}</h3>
              {!compact && <p>{a.body}</p>}
            </article>
          ))}
        </div>
      )}
    </>
  );
}
